import { format } from 'date-fns';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { cn } from '../lib/utils';
import type { CustomRange } from './types';

const YMD = 'yyyy-MM-dd';
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

const CALENDAR_CLASS_NAMES = {
  months:
    'relative flex flex-col gap-4 md:flex-row md:gap-6 md:divide-x md:divide-surface-200/70',
  month: 'flex w-full flex-col gap-3 md:[&:not(:first-child)]:pl-6',
  dropdowns:
    'flex h-(--cell-size) w-full items-center justify-center gap-3 font-serif text-[14px] font-medium text-surface-900',
  dropdown_root:
    'relative rounded-md px-1.5 py-0.5 hover:bg-surface-100 transition-colors has-focus:ring-2 has-focus:ring-surface-900/15',
  weekday:
    'text-surface-600 flex-1 select-none font-mono text-[10px] font-semibold uppercase tracking-[1.5px]',
  range_start: 'rounded-l-full bg-surface-200/70',
  range_middle: 'bg-surface-200/70 rounded-none',
  range_end: 'rounded-r-full bg-surface-200/70',
  today: 'font-semibold text-neg data-[selected=true]:text-surface-0',
  outside: 'text-surface-300 aria-selected:text-surface-400',
  disabled: 'text-surface-400 line-through decoration-surface-300 opacity-70',
  day: 'group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-full [&:last-child[data-selected=true]_button]:rounded-r-full',
};

function formatRangeHeader(range: DateRange | undefined): string {
  if (range?.from && range?.to) {
    return range.from.getTime() === range.to.getTime()
      ? format(range.from, 'MMMM d, yyyy')
      : `${format(range.from, 'MMM d')} – ${format(range.to, 'MMM d, yyyy')}`;
  }
  if (range?.from) return `${format(range.from, 'MMM d, yyyy')} — end date optional`;
  return 'Pick a start date';
}

function parseYmd(s: string): Date | null {
  if (!YMD_RE.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const dt = new Date(y!, m! - 1, d!);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatRangeShort(from: string, to: string): string {
  const a = parseYmd(from);
  const b = parseYmd(to);
  if (!a || !b) return `${from} → ${to}`;
  if (from === to) return format(a, 'MMM d, yyyy');
  const sameYear = a.getFullYear() === b.getFullYear();
  const fmt = sameYear ? 'MMM d' : 'MMM d, yyyy';
  return `${format(a, fmt)} – ${format(b, 'MMM d, yyyy')}`;
}

export function CustomRangePopover({
  selected,
  currentRange,
  onApply,
}: {
  selected: boolean;
  currentRange: { from: string; to: string } | null;
  onApply: (range: CustomRange) => void;
}) {
  const [open, setOpen] = useState(false);

  function rangeFromIso(src: { from: string; to: string } | null): DateRange | undefined {
    if (!src) return undefined;
    const from = parseYmd(src.from);
    const to = parseYmd(src.to);
    if (!from || !to) return undefined;
    return { from, to };
  }

  const [range, setRange] = useState<DateRange | undefined>(() =>
    rangeFromIso(currentRange),
  );

  function handleOpenChange(next: boolean): void {
    if (next) setRange(rangeFromIso(currentRange));
    setOpen(next);
  }

  const invalid = !range?.from;
  const today = new Date();

  function handleSelectRange(next: DateRange | undefined): void {
    setRange(next);
  }

  function handleApply(): void {
    if (!range?.from) {
      toast.error('Pick at least a start date.');
      return;
    }
    const fromDate = range.from;
    const toDate = range.to ?? range.from;
    try {
      onApply({
        from: format(fromDate, YMD),
        to: format(toDate, YMD),
      });
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Pick at least a start date.');
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="tab"
          aria-selected={selected}
          tabIndex={selected ? 0 : -1}
          className={cn(
            'relative py-3 text-[13px] font-medium transition-colors cursor-pointer',
            'focus-visible:outline-none focus-visible:text-surface-900',
            selected
              ? 'text-surface-900'
              : 'text-surface-500 hover:text-surface-900',
          )}
        >
          {selected && currentRange
            ? formatRangeShort(currentRange.from, currentRange.to)
            : 'Custom'}
          {selected && (
            <span
              aria-hidden
              className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-7 rounded-full bg-neg"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-auto rounded-[6px] border border-surface-300/70 bg-surface-0 p-0 shadow-xl"
      >
        <div className="flex flex-col">
          <div className="flex flex-col gap-1.5 border-b border-surface-200/70 px-5 pt-4 pb-4">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[2px] text-surface-600">
              Custom date range
            </span>
            <span className="font-serif text-[16px] leading-tight tracking-tight text-surface-900">
              {formatRangeHeader(range)}
            </span>
          </div>
          <div className="px-3 pt-2 pb-1">
            <Calendar
              mode="range"
              selected={range}
              onSelect={handleSelectRange}
              numberOfMonths={2}
              showOutsideDays={false}
              defaultMonth={range?.from ?? today}
              disabled={{ after: today }}
              captionLayout="dropdown"
              classNames={CALENDAR_CLASS_NAMES}
              className="bg-surface-0 p-2"
            />
          </div>
          {invalid && (
            <div
              id="custom-range-hint"
              role="status"
              className="px-5 pt-1 text-[12px] italic text-surface-600"
            >
              Pick at least a start date.
            </div>
          )}
          <div className="flex items-center justify-end gap-2 border-t border-surface-200/70 px-4 py-3">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => setRange(undefined)}
              disabled={!range?.from}
              className="text-surface-500 hover:text-surface-900"
            >
              Clear
            </Button>
            <Button
              size="sm"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleApply();
              }}
              disabled={invalid}
              aria-describedby={invalid ? 'custom-range-hint' : undefined}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
